import React, { useState, useCallback, useMemo } from 'react';
import { Plus, Edit2, Trash2, Clock, CheckSquare, Square, Sun, Sunset, Moon } from 'lucide-react';
import toast from 'react-hot-toast';
import Table from '../components/Table';
import Modal from '../components/Modal';
import Form, { FormField, useForm } from '../components/Form';
import FilterBar from '../components/FilterBar';
import { StatusBadge, StatusToggleCell } from '../components/StatusBadge';
import { useFetch, useFilters } from '../hooks';
import { timeSlotService } from '../services/api';

/* ── Helpers ────────────────────────────────────────────────────────────────── */
const to12h = (time24) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
};

const formatSlotLabel = (start, end) =>
  start && end ? `${to12h(start)} – ${to12h(end)}` : '';

const getPeriod = (startTime) => {
  const h = parseInt(startTime?.split(':')[0] || '0');
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
};

const PERIOD_META = {
  morning:   { label: 'Morning',   icon: Sun,    color: 'text-yellow-600 dark:text-yellow-400',  bg: 'bg-yellow-50 dark:bg-yellow-900/20'  },
  afternoon: { label: 'Afternoon', icon: Sunset,  color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20'  },
  evening:   { label: 'Evening',   icon: Moon,    color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/20'  },
};

/* ── Filter config ──────────────────────────────────────────────────────────── */
const FILTER_INITIAL = { search: '', status: '', period: '' };
const FILTER_FIELDS = [
  {
    key: 'status', label: 'Status', type: 'select',
    options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }],
  },
  {
    key: 'period', label: 'Period', type: 'select',
    options: [
      { value: 'morning',   label: 'Morning (before 12 PM)'   },
      { value: 'afternoon', label: 'Afternoon (12–5 PM)'       },
      { value: 'evening',   label: 'Evening (after 5 PM)'      },
    ],
  },
];
const slotFilterFn = (item, f) => {
  if (f.search && !item.label.toLowerCase().includes(f.search.toLowerCase())) return false;
  if (f.status && item.status !== f.status) return false;
  if (f.period && item.period !== f.period) return false;
  return true;
};

/* ── Time Slot Form ─────────────────────────────────────────────────────────── */
const TimeSlotForm = ({ initial, allSlots, onSubmit, onClose }) => {
  const [overlapError, setOverlapError] = useState('');

  const rules = { startTime: { required: true }, endTime: { required: true } };
  const { values, errors, handleChange, handleBlur, handleSubmit } = useForm(
    initial
      ? { startTime: initial.startTime, endTime: initial.endTime, status: initial.status }
      : { startTime: '', endTime: '', status: 'active' },
    rules
  );

  const previewLabel = formatSlotLabel(values.startTime, values.endTime);
  const period = getPeriod(values.startTime);
  const PeriodIcon = period ? PERIOD_META[period]?.icon : null;

  const handleFormSubmit = (vals) => {
    setOverlapError('');
    if (vals.endTime <= vals.startTime) {
      setOverlapError('End time must be after start time');
      return;
    }
    // Check overlap (exclude self when editing)
    const hasOverlap = timeSlotService.checkOverlap(vals.startTime, vals.endTime, initial?.id);
    if (hasOverlap) {
      setOverlapError('This time slot overlaps with an existing slot');
      return;
    }
    const label = formatSlotLabel(vals.startTime, vals.endTime);
    onSubmit({ ...vals, label, period: getPeriod(vals.startTime) });
  };

  return (
    <Form onSubmit={handleSubmit(handleFormSubmit)}>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Start Time" error={errors.startTime} required>
          <input
            name="startTime"
            type="time"
            value={values.startTime}
            onChange={handleChange}
            onBlur={handleBlur}
            className="input-field"
          />
        </FormField>
        <FormField label="End Time" error={errors.endTime} required>
          <input
            name="endTime"
            type="time"
            value={values.endTime}
            onChange={handleChange}
            onBlur={handleBlur}
            className="input-field"
          />
        </FormField>
      </div>

      {/* Live preview */}
      {previewLabel && (
        <div className={`flex items-center gap-2.5 px-4 py-3 rounded-xl ${period ? PERIOD_META[period].bg : 'bg-gray-50 dark:bg-gray-700/50'}`}>
          {PeriodIcon && <PeriodIcon className={`w-4 h-4 shrink-0 ${period ? PERIOD_META[period].color : ''}`} />}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Preview</p>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{previewLabel}</p>
            {period && <p className={`text-xs font-medium mt-0.5 capitalize ${PERIOD_META[period].color}`}>{PERIOD_META[period].label}</p>}
          </div>
        </div>
      )}

      {overlapError && (
        <p className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">{overlapError}</p>
      )}

      <FormField label="Status">
        <div className="flex gap-3">
          {['active', 'inactive'].map(s => (
            <label key={s} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="status" value={s} checked={values.status === s} onChange={handleChange} className="accent-blue-600" />
              <StatusBadge status={s} dot={false} />
            </label>
          ))}
        </div>
      </FormField>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary">{initial ? 'Update Slot' : 'Add Time Slot'}</button>
      </div>
    </Form>
  );
};

/* ── Period Group Header ────────────────────────────────────────────────────── */
const PeriodHeader = ({ period, count }) => {
  const meta = PERIOD_META[period];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <div className={`flex items-center gap-2 px-4 py-2 ${meta.bg} border-b border-gray-200 dark:border-gray-700`}>
      <Icon className={`w-4 h-4 ${meta.color}`} />
      <span className={`text-xs font-semibold uppercase tracking-wider ${meta.color}`}>{meta.label}</span>
      <span className="text-xs text-gray-400 ml-1">({count} slots)</span>
    </div>
  );
};

/* ── Page ───────────────────────────────────────────────────────────────────── */
const TimeSlots = () => {
  const { data: slots, loading, refetch } = useFetch(() => timeSlotService.getAll());
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState([]);
  const [groupByPeriod, setGroupByPeriod] = useState(true);

  const filterFnCb = useCallback(slotFilterFn, []);
  const { filters, onChange: onFilterChange, onReset, filtered, activeCount } = useFilters(FILTER_INITIAL, filterFnCb, slots);

  /* Sort by start time */
  const sorted = useMemo(() => {
    if (!filtered) return [];
    return [...filtered].sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [filtered]);

  /* Group by period */
  const grouped = useMemo(() => {
    if (!groupByPeriod) return null;
    return ['morning', 'afternoon', 'evening'].reduce((acc, p) => {
      const items = sorted.filter(s => s.period === p);
      if (items.length) acc[p] = items;
      return acc;
    }, {});
  }, [sorted, groupByPeriod]);

  /* ── Handlers ── */
  const handleSave = async (vals) => {
    try {
      if (modal === 'edit') {
        await timeSlotService.update(selected.id, vals);
        toast.success('Time slot updated!');
      } else {
        await timeSlotService.create({ ...vals, doctorCount: 0 });
        toast.success('Time slot added!');
      }
      refetch(); setModal(null); setSelected(null);
    } catch { toast.error('Something went wrong'); }
  };

  const handleDelete = async (id) => {
    const slot = slots?.find(s => s.id === id);
    if (slot?.doctorCount > 0) { toast.error(`Cannot delete — ${slot.doctorCount} doctor(s) using this slot`); return; }
    if (!window.confirm('Delete this time slot?')) return;
    try { await timeSlotService.delete(id); toast.success('Deleted'); refetch(); setChecked(c => c.filter(x => x !== id)); }
    catch { toast.error('Failed to delete'); }
  };

  const handleToggle = async (slot) => {
    try { await timeSlotService.toggleStatus(slot.id); toast.success('Status updated'); refetch(); }
    catch { toast.error('Failed to update'); }
  };

  /* Bulk */
  const allChecked = sorted.length > 0 && sorted.every(s => checked.includes(s.id));
  const toggleAll = () => setChecked(allChecked ? [] : sorted.map(s => s.id));
  const toggleOne = (id) => setChecked(c => c.includes(id) ? c.filter(x => x !== id) : [...c, id]);

  const handleBulk = async (status) => {
    if (!checked.length) return;
    try {
      await timeSlotService.bulkToggle(checked, status);
      toast.success(`${checked.length} slot(s) ${status}`);
      refetch(); setChecked([]);
    } catch { toast.error('Bulk update failed'); }
  };

  /* ── Columns ── */
  const columns = [
    {
      key: 'id', label: '', render: (id) => (
        <button onClick={() => toggleOne(id)} className="text-gray-400 hover:text-medihub-primary transition-colors">
          {checked.includes(id) ? <CheckSquare className="w-4 h-4 text-medihub-primary" /> : <Square className="w-4 h-4" />}
        </button>
      )
    },
    {
      key: 'label', label: 'Time Slot', render: (v, row) => {
        const meta = PERIOD_META[row.period];
        const Icon = meta?.icon || Clock;
        return (
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${meta?.bg || 'bg-gray-100 dark:bg-gray-700'}`}>
              <Icon className={`w-4 h-4 ${meta?.color || 'text-gray-500'}`} />
            </div>
            <span className="font-medium text-gray-900 dark:text-white font-mono text-sm">{v}</span>
          </div>
        );
      }
    },
    {
      key: 'period', label: 'Period', render: (v) => {
        const meta = PERIOD_META[v];
        if (!meta) return '—';
        return (
          <span className={`inline-flex items-center gap-1 text-xs font-medium capitalize ${meta.color}`}>
            <span className={`w-2 h-2 rounded-full ${meta.bg.replace('bg-', 'bg-').split(' ')[0]}`} />
            {meta.label}
          </span>
        );
      }
    },
    {
      key: 'doctorCount', label: 'Doctors', render: v => (
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-300">{v}</span>
      )
    },
    {
      key: 'status', label: 'Status', render: (v, row) => (
        <StatusToggleCell active={v === 'active'} onToggle={() => handleToggle(row)} />
      )
    },
    {
      key: 'id', label: 'Actions', render: (_, row) => (
        <div className="flex items-center gap-1">
          <button onClick={() => { setSelected(row); setModal('edit'); }} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500" title="Edit">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={() => handleDelete(row.id)} className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors text-red-500" title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    },
  ];

  /* Stats */
  const activeSlots   = slots?.filter(s => s.status === 'active').length || 0;
  const inactiveSlots = slots?.filter(s => s.status === 'inactive').length || 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Time Slots</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Manage doctor availability time slots</p>
        </div>
        <button onClick={() => { setSelected(null); setModal('add'); }} className="btn-primary flex items-center gap-1.5">
          <Plus className="w-4 h-4" />Add Time Slot
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total',     value: slots?.length || 0, color: 'text-gray-900 dark:text-white' },
          { label: 'Active',    value: activeSlots,         color: 'text-green-700 dark:text-green-400' },
          { label: 'Inactive',  value: inactiveSlots,       color: 'text-red-600 dark:text-red-400' },
          { label: 'Morning',   value: slots?.filter(s => s.period === 'morning').length || 0,   color: 'text-yellow-600 dark:text-yellow-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card p-4 text-center">
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        onChange={onFilterChange}
        onReset={onReset}
        searchPlaceholder="Search time slot…"
        fields={FILTER_FIELDS}
        activeCount={activeCount}
      />

      {/* Bulk actions */}
      {checked.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-medihub-light dark:bg-medihub-dark/20 border border-medihub-soft dark:border-medihub-dark rounded-xl">
          <span className="text-sm font-medium text-medihub-secondary dark:text-medihub-highlight">{checked.length} selected</span>
          <div className="flex gap-2 ml-auto">
            <button onClick={() => handleBulk('active')} className="text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium">Activate All</button>
            <button onClick={() => handleBulk('inactive')} className="text-xs px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium">Deactivate All</button>
            <button onClick={() => setChecked([])} className="text-xs px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium">Clear</button>
          </div>
        </div>
      )}

      {/* Group toggle */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {sorted.length} slot{sorted.length !== 1 ? 's' : ''}{activeCount ? ` of ${slots?.length || 0}` : ''}
        </span>
        <div className="flex items-center gap-2">
          <button onClick={toggleAll} className="flex items-center gap-1 text-xs text-gray-500 hover:text-medihub-primary dark:hover:text-medihub-highlight transition-colors">
            {allChecked ? <CheckSquare className="w-3.5 h-3.5 text-medihub-primary" /> : <Square className="w-3.5 h-3.5" />}
            Select All
          </button>
          <span className="text-gray-300 dark:text-gray-600">|</span>
          <button
            onClick={() => setGroupByPeriod(v => !v)}
            className={`text-xs px-2.5 py-1 rounded-lg border transition-colors font-medium ${groupByPeriod ? 'bg-medihub-primary text-white border-medihub-primary' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
          >
            {groupByPeriod ? 'Grouped' : 'Group by Period'}
          </button>
        </div>
      </div>

      {/* Grouped view */}
      {groupByPeriod && grouped ? (
        <div className="card overflow-hidden">
          {Object.entries(grouped).map(([period, items], idx) => (
            <div key={period}>
              {idx > 0 && <div className="border-t border-gray-200 dark:border-gray-700" />}
              <PeriodHeader period={period} count={items.length} />
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                    {items.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        {columns.map(col => (
                          <td key={col.key} className="px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                            {col.render ? col.render(row[col.key], row) : row[col.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {sorted.length === 0 && !loading && (
            <div className="py-14 text-center text-gray-400 dark:text-gray-500 text-sm">No time slots found</div>
          )}
        </div>
      ) : (
        <Table
          title={`Time Slots (${sorted.length})`}
          columns={columns}
          data={sorted}
          loading={loading}
          exportFilename="time-slots"
        />
      )}

      {/* Add / Edit Modal */}
      <Modal
        isOpen={modal === 'add' || modal === 'edit'}
        onClose={() => { setModal(null); setSelected(null); }}
        title={modal === 'edit' ? 'Edit Time Slot' : 'Add Time Slot'}
        size="sm"
      >
        <TimeSlotForm
          initial={modal === 'edit' ? selected : null}
          allSlots={slots || []}
          onSubmit={handleSave}
          onClose={() => { setModal(null); setSelected(null); }}
        />
      </Modal>
    </div>
  );
};

export default TimeSlots;
